/**
 * InboxDetailFooter — the action bar that closes an opened update.
 *
 * One surface for every item kind: a hairline rule, a quieter fill than the
 * reading pane (the same relationship the shared Dialog footer has with its
 * panel), and the body's own horizontal padding and text column, so the
 * buttons line up with the paragraph above them instead of floating.
 *
 * Layout is deliberate: DOM order is primary-first, so keyboard and screen
 * reader users reach the decision immediately, while `flex-row-reverse` puts
 * that same button on the right where a pointer expects it. Under `sm` the
 * row becomes a full-width stack with the primary on top. Children own their
 * variants — exactly one `default`, the rest `outline`/`ghost` — and get a
 * single consistent width rule here rather than repeating it per button.
 *
 * @module components/inbox/InboxDetailFooter
 */
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";

export function InboxDetailFooter({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    // Not a bar: a row of actions at the end of the update, part of its text
    // column, so it reads like the last paragraph rather than a dialog footer.
    <footer
      data-inbox-detail-footer=""
      className={cn("mt-10 flex flex-wrap items-center gap-2", className)}
    >
      {children}
    </footer>
  );
}
